import { EventCategory } from "@qendo/calendar";
import {
    CycleKey,
    DefaultNotificationSettings,
    type NotificationData,
    NotificationTriggerGroup,
    type ScheduleNotificationRequest,
} from "@qendo/notifications";
import type { TenScaleData } from "@qendo/profile/journal";
import { LegacySymptomId } from "@qendo/trackables";
import { addDays, compareDesc, type DateArg, isFuture, subDays, toDate } from "date-fns";
import { Platform } from "expo-modules-core";
import * as Notifications from "expo-notifications";
import { type NotificationRequest, PermissionStatus } from "expo-notifications";
import { type Href, router } from "expo-router";
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from "react";
import type { QendoIconSet } from "~/components/QendoIcon";
import { NotificationSettings } from "~/entities/notification-settings";
import { useJournal } from "~/providers/JournalProvider";
import { useProfile } from "~/providers/ProfileProvider";
import { useTrackable } from "~/providers/TrackableProvider";
import { formatDateString, getWeekFromDate, mergeDateTime } from "~/util/dates";
import { extractPeriods } from "~/util/qendo/cycle";
import {
    extractWeekDates,
    filterSchedule,
    findScheduled,
    hasFlare,
    hasScheduled,
    makeCalendarNotification,
    makeCycleNotification,
    makeFlareNotification,
    makeKey,
    makePainNotification,
    makeScheduledNotification,
    notifyOnFlare,
    notifyOnSchedule,
    shouldBeScheduled,
} from "~/util/qendo/notifications";
import { NotificationKey, notificationStorage } from "~/util/secure-storage";

export type NotificationsState = {
    granted: boolean;
    status: PermissionStatus;
    settings: NotificationSettings;
    notifications: NotificationData[];
    hasUnseen: boolean;
    canAskAgain: boolean;
    checkSystemSettings(): Promise<void>;
    markSeen(id: string): void;
    markUnSeen(id: string): void;
    dismiss(id: string): void;
    dismissSeen(): void;
    requestPermissions(): Promise<boolean>;
    schedule(request: ScheduleNotificationRequest): Promise<string | undefined>;
    updateSettings(value: Partial<NotificationSettings>): void;
};

export const NotificationsContext = createContext<NotificationsState>({
    granted: false,
    status: PermissionStatus.UNDETERMINED,
    settings: new NotificationSettings(),
    notifications: [],
    hasUnseen: false,
    canAskAgain: false,
    checkSystemSettings: async () => {
        return;
    },
    markSeen: (_id) => {
        return;
    },
    markUnSeen: (_id) => {
        return;
    },
    dismiss: (_id) => {
        return;
    },
    dismissSeen: () => {
        return;
    },
    requestPermissions: async () => false,
    schedule: async (_request) => undefined,
    updateSettings: (_value) => {
        return;
    },
});

export function useNotifications() {
    const value = useContext(NotificationsContext);
    if (!value) {
        throw new Error("useNotifications must be wrapped in a <NotificationsProvider />");
    }
    return value;
}

export function useNotificationObserver() {
    useEffect(() => {
        if (Platform.select({ android: false, ios: false, default: true })) {
            return;
        }

        let isMounted = true;

        function redirect(notification: Notifications.Notification) {
            const url = notification.request.content.data?.href as Href | undefined;
            if (url) {
                router.push(url);
            }
        }

        Notifications.getLastNotificationResponseAsync().then((response) => {
            if (!isMounted || !response?.notification) {
                return;
            }
            redirect(response?.notification);
        });

        const subscription = Notifications.addNotificationResponseReceivedListener((response) => {
            redirect(response.notification);
        });

        return () => {
            isMounted = false;
            subscription.remove();
        };
    }, []);
}

const HIGH_PAIN_THRESHOLD = 7;
const REPEAT_PAIN_THRESHOLD = 5;

export function NotificationsProvider({ children }: { children: ReactNode }) {
    const [mounted, setMounted] = useState(false);
    const [granted, setGranted] = useState(false);
    const [canAskAgain, setCanAskAgain] = useState(true);
    const [status, setStatus] = useState<PermissionStatus>(PermissionStatus.UNDETERMINED);
    const [settings, setSettings] = useState<NotificationSettings>(
        NotificationSettings.from(DefaultNotificationSettings),
    );
    const [notifications, setNotifications] = useState<NotificationData[]>([]);
    const [scheduling, setScheduling] = useState(false);
    const [pain, setPain] = useState(0);
    const [lastFlare, setLastFlare] = useState<DateArg<Date> | null>(null);
    const [scheduled, setScheduled] = useState<NotificationRequest[]>([]);
    const { findTrackable } = useTrackable();
    const { modified, journalsForDay, eventsForDay } = useJournal();
    const { is_primary, cycle } = useProfile();
    const pain_trackable = useMemo(() => findTrackable(LegacySymptomId.pain)?.id ?? "none", [findTrackable]);
    const today = formatDateString(new Date());

    const notifications_enabled = mounted && granted && settings.push_notifications;
    const journals_today = useMemo(
        () => (is_primary ? journalsForDay(today) : []),
        [journalsForDay, today, is_primary],
    );
    const journals_past_week = useMemo(
        () =>
            is_primary
                ? [
                      ...journalsForDay(subDays(today, 6)),
                      ...journalsForDay(subDays(today, 5)),
                      ...journalsForDay(subDays(today, 4)),
                      ...journalsForDay(subDays(today, 3)),
                      ...journalsForDay(subDays(today, 2)),
                      ...journalsForDay(subDays(today, 1)),
                      ...journalsForDay(today),
                  ]
                : [],
        [journalsForDay, today, is_primary],
    );

    const upcoming_events = useMemo(
        () =>
            is_primary
                ? [
                      ...eventsForDay(today),
                      ...eventsForDay(addDays(today, 1)),
                      ...eventsForDay(addDays(today, 2)),
                      ...eventsForDay(addDays(today, 3)),
                  ].filter(({ category }) => category === EventCategory.appointment)
                : [],
        [eventsForDay, today, is_primary],
    );

    const notify_on_last_flare = useMemo(
        () => (notifications_enabled ? notifyOnFlare(settings) : []),
        [notifications_enabled, settings],
    );

    const notify_on_schedule = useMemo(
        () => (notifications_enabled ? notifyOnSchedule(settings) : []),
        [notifications_enabled, settings],
    );

    const schedule = async (request: ScheduleNotificationRequest) => {
        return await requestPermissions().then(async (granted) => {
            if (granted) {
                return await Notifications.scheduleNotificationAsync({
                    trigger: null,
                    ...request,
                });
            }
        });
    };

    const syncNotifications = (to_schedule: ScheduleNotificationRequest[], to_remove: string[]) => {
        if (!to_schedule.length && !to_remove.length) {
            return;
        }
        setScheduling(true);
        Promise.allSettled([
            ...to_schedule.map(schedule),
            ...to_remove.map((identifier) => Notifications.cancelScheduledNotificationAsync(identifier)),
        ])
            .then(loadSchedule)
            .finally(() => setScheduling(false));
    };

    useEffect(() => {
        setLastFlare(hasFlare(journals_today) ? today : null);
    }, [journals_today, today]);

    const loadSchedule = async () => {
        await Notifications.getAllScheduledNotificationsAsync().then(setScheduled);
    };

    const requestPermissions = async () => {
        if (granted || !canAskAgain) {
            return granted;
        }

        return await Notifications.requestPermissionsAsync()
            .then(({ status, granted, canAskAgain }) => {
                setStatus(status);
                setGranted(granted);
                setCanAskAgain(canAskAgain);

                return granted;
            })
            .catch(() => {
                setStatus(PermissionStatus.DENIED);
                setGranted(false);
                setCanAskAgain(false);

                return false;
            });
    };

    const updateSettings = (value: Partial<NotificationSettings> | null) => {
        const current = NotificationSettings.from(settings);

        current.fill(value ?? { push_notifications: granted });

        setSettings(current);

        if (mounted && current.dirty()) {
            notificationStorage.setItem(NotificationKey.notification_settings, current);
        }
    };

    const markSeen = (id: string) => {
        setNotifications((current) =>
            current.map((item) => {
                if (item.identifier === id) {
                    item.seen = true;
                }

                return item;
            }),
        );
    };

    const markUnSeen = (id: string) => {
        setNotifications((current) =>
            current.map((item) => {
                if (item.identifier === id) {
                    item.seen = false;
                }

                return item;
            }),
        );
    };

    const dismiss = (id: string) => {
        setNotifications((current) => current.filter((value) => value.identifier !== id));
    };

    const dismissSeen = () => {
        setNotifications((current) => current.filter(({ seen }) => !seen));
    };

    const hasUnseen = notifications.some(({ seen }) => !seen);

    useEffect(() => {
        if (mounted) {
            notificationStorage.setItem(NotificationKey.notifications, notifications);
        }
    }, [mounted, notifications]);

    const checkSystemSettings = async () => {
        await Notifications.getPermissionsAsync()
            .then(({ status, granted, canAskAgain }) => {
                setStatus(status);
                setGranted(granted);
                setCanAskAgain(canAskAgain);
            })
            .catch(() => {
                setStatus(PermissionStatus.DENIED);
                setGranted(false);
                setCanAskAgain(false);
            });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(checkSystemSettings): setter
    // biome-ignore lint/correctness/useExhaustiveDependencies(updateSettings): setter
    // biome-ignore lint/correctness/useExhaustiveDependencies(loadSchedule): setter
    useEffect(() => {
        Notifications.setNotificationHandler({
            handleNotification: async (payload) => {
                const notification: NotificationData = {
                    seen: false,
                    identifier: payload.request.identifier,
                    date: payload.date,
                    href: (payload.request.content.data?.href as Href) ?? undefined,
                    icon: (payload.request.content.data?.icon as QendoIconSet) ?? "heart",
                    title: payload.request.content.title,
                    subtitle: payload.request.content.subtitle,
                    body: payload.request.content.body,
                    data: payload.request.content.data,
                };

                setNotifications((notifications: NotificationData[]) => {
                    return [...notifications, notification]
                        .sort((a, b) => compareDesc(a.date, b.date))
                        .filter(
                            ({ identifier }, idx, array) =>
                                array.findIndex((el) => el.identifier === identifier) === idx,
                        );
                });

                loadSchedule();

                return {
                    shouldShowBanner: true,
                    shouldShowList: true,
                    shouldPlaySound: false,
                    shouldSetBadge: true,
                };
            },
        });

        Promise.allSettled([
            checkSystemSettings(),
            loadSchedule(),
            notificationStorage.getItem<NotificationSettings>(NotificationKey.notification_settings).then((value) => {
                updateSettings(value ?? DefaultNotificationSettings);
            }),
            notificationStorage
                .getItem<NotificationData[]>(NotificationKey.notifications)
                .then((notifications) => setNotifications(notifications ?? [])),
        ]).finally(() => setMounted(true));

        return () => {
            setMounted(false);
            Notifications.setNotificationHandler(null);
        };
    }, []);

    const pain_records_in_past_week = useMemo(
        () =>
            journals_past_week.filter(
                (j) =>
                    j.trackable_id === pain_trackable && ((j.scale as TenScaleData)?.value ?? 0) >= HIGH_PAIN_THRESHOLD,
            ).length,
        [journals_past_week, pain_trackable],
    );

    const tracking_events = notifications_enabled && settings.upcoming_appointments && is_primary;
    const tracking_pain = notifications_enabled && settings.pain && is_primary;
    const can_notify_pain =
        tracking_pain &&
        modified &&
        pain < REPEAT_PAIN_THRESHOLD &&
        !hasScheduled(scheduled, "", NotificationTriggerGroup.pain);

    /* Process pain notifications */
    // biome-ignore lint/correctness/useExhaustiveDependencies(loadSchedule): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(schedule): action
    useEffect(() => {
        if (tracking_pain) {
            if (can_notify_pain && pain_records_in_past_week >= REPEAT_PAIN_THRESHOLD) {
                schedule(makePainNotification()).then(loadSchedule);
            }
            setPain(pain_records_in_past_week);
        }
    }, [tracking_pain, can_notify_pain, pain_records_in_past_week]);

    /* Process flare notifications */
    // biome-ignore lint/correctness/useExhaustiveDependencies(syncNotifications): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(scheduled): current schedule
    // biome-ignore lint/correctness/useExhaustiveDependencies(mounted): state
    useEffect(() => {
        if (!modified || !mounted || !is_primary) {
            return;
        }

        const tomorrow = formatDateString(addDays(today, 1));
        const current = filterSchedule(scheduled, makeKey("", NotificationTriggerGroup.flare, tomorrow));

        const to_schedule: ScheduleNotificationRequest[] = [];
        const to_remove: string[] = [];

        if (lastFlare === today && notify_on_last_flare.length && notifications_enabled) {
            for (const { key, times } of notify_on_last_flare) {
                for (const { time } of times) {
                    if (!hasScheduled(scheduled, key, NotificationTriggerGroup.flare, tomorrow, time)) {
                        to_schedule.push(makeFlareNotification(key, time));
                    }
                }
            }
        }

        if (current.length) {
            for (const { identifier } of current) {
                if (
                    !notifications_enabled ||
                    !notify_on_last_flare.find(({ key, times }) =>
                        shouldBeScheduled(identifier, key, NotificationTriggerGroup.flare, times, [tomorrow]),
                    )
                ) {
                    to_remove.push(identifier);
                }
            }
        }

        syncNotifications(to_schedule, to_remove);
    }, [notifications_enabled, lastFlare, is_primary, modified, today, notify_on_last_flare]);

    /* Process cycle notifications */
    // biome-ignore lint/correctness/useExhaustiveDependencies(syncNotifications): action
    // biome-ignore lint/correctness/useExhaustiveDependencies(scheduled): current schedule
    // biome-ignore lint/correctness/useExhaustiveDependencies(mounted): state
    useEffect(() => {
        if (!mounted || !is_primary || !cycle.tracked || !cycle.periods.length) {
            return;
        }

        const to_schedule: ScheduleNotificationRequest[] = [];
        const to_remove: string[] = [];

        const { prev, current, next } = extractPeriods(cycle, today);
        const cycle_length = (cycle.estimate ? cycle.estimated_cycle : null) ?? cycle.cycle;
        const ovulation_days = Math.floor(cycle_length / 2);

        if (cycle_length > 1 && (prev || current || next)) {
            const current_cycle = current ?? prev;
            const cycle_started = current_cycle?.start_at ?? null;
            const period_ended = Boolean(current_cycle?.ended_at);
            const next_cycle = period_ended ? next : null;
            const next_period = next_cycle ? toDate(next_cycle.start_at) : null;
            const next_prediction = next_period ? subDays(next_period, Math.min(cycle_length, 4)) : null;
            const next_ovulation =
                cycle_started && isFuture(addDays(cycle_started, ovulation_days))
                    ? addDays(cycle_started, ovulation_days)
                    : next_period && addDays(next_period, ovulation_days);

            const notifications: [CycleKey, Date | null][] = [
                [CycleKey.prediction, next_prediction],
                [CycleKey.start, next_period],
                [CycleKey.ovulation, next_ovulation],
            ];

            for (const [key, date] of notifications) {
                const enabled =
                    notifications_enabled &&
                    date &&
                    (key === CycleKey.ovulation ? settings.ovulation_prediction : settings.period_prediction);

                if (enabled && hasScheduled(scheduled, key, NotificationTriggerGroup.cycle, date)) {
                    continue;
                }

                const next = findScheduled(scheduled, key, NotificationTriggerGroup.cycle);

                if (next) {
                    to_remove.push(next.identifier);
                }

                if (enabled && isFuture(date)) {
                    to_schedule.push(makeCycleNotification(key, date));
                }
            }
        }

        syncNotifications(to_schedule, to_remove);
    }, [is_primary, cycle, today, settings, notifications_enabled]);

    const scheduled_daily = filterSchedule(scheduled, `|${NotificationTriggerGroup.daily}|`).map(
        ({ identifier }) => identifier,
    );
    const scheduled_weekly = filterSchedule(scheduled, `|${NotificationTriggerGroup.specific_days}|`).map(
        ({ identifier }) => identifier,
    );
    const scheduled_interval = filterSchedule(scheduled, `|${NotificationTriggerGroup.interval}|`).map(
        ({ identifier }) => identifier,
    );
    const scheduled_appointments = filterSchedule(scheduled, `|${NotificationTriggerGroup.calendar}|`).map(
        ({ identifier }) => identifier,
    );

    /* Process scheduled notifications */
    // biome-ignore lint/correctness/useExhaustiveDependencies(scheduled): state
    // biome-ignore lint/correctness/useExhaustiveDependencies(syncNotifications): action
    useEffect(() => {
        if (!mounted || scheduling) {
            return;
        }

        const to_schedule: ScheduleNotificationRequest[] = [];
        const to_remove: string[] = [];

        const week = getWeekFromDate(today);
        const dates: DateArg<Date>[] = [today, ...Object.values(week)];

        if (notify_on_schedule.length && notifications_enabled) {
            for (const { key, frequency, times, days, interval } of notify_on_schedule) {
                switch (frequency) {
                    case NotificationTriggerGroup.daily: {
                        for (const { time } of times) {
                            for (const date of dates) {
                                if (
                                    isFuture(mergeDateTime(date, time)) &&
                                    !hasScheduled(scheduled, key, frequency, date, time)
                                ) {
                                    to_schedule.push(makeScheduledNotification(key, frequency, time, date));
                                }
                            }
                        }
                        break;
                    }
                    case NotificationTriggerGroup.specific_days: {
                        const dates = extractWeekDates(week, days);

                        for (const date of dates) {
                            for (const { time } of times) {
                                if (!hasScheduled(scheduled, key, frequency, date, time)) {
                                    to_schedule.push(makeScheduledNotification(key, frequency, time, date));
                                }
                            }
                        }
                        break;
                    }
                    case NotificationTriggerGroup.interval: {
                        const date = addDays(today, interval);

                        for (const { time } of times) {
                            if (!hasScheduled(scheduled, key, frequency, date, time)) {
                                to_schedule.push(makeScheduledNotification(key, frequency, time, date));
                            }
                        }
                    }
                }
            }
        }

        if (upcoming_events.length && tracking_events) {
            for (const appointment of upcoming_events) {
                const notify_on = subDays(appointment.start_at, 1);

                if (
                    isFuture(notify_on) &&
                    !hasScheduled(scheduled, appointment.id, NotificationTriggerGroup.calendar, notify_on)
                ) {
                    to_schedule.push(makeCalendarNotification(appointment, notify_on));
                }
            }
        }

        const daily_schedule = notify_on_schedule.filter(
            ({ frequency }) => frequency === NotificationTriggerGroup.daily,
        );
        const weekly_schedule = notify_on_schedule.filter(
            ({ frequency }) => frequency === NotificationTriggerGroup.specific_days,
        );
        const interval_schedule = notify_on_schedule.filter(
            ({ frequency }) => frequency === NotificationTriggerGroup.interval,
        );

        for (const identifier of scheduled_daily) {
            if (
                !notifications_enabled ||
                !daily_schedule.length ||
                !daily_schedule.some(({ key, times }) =>
                    shouldBeScheduled(identifier, key, NotificationTriggerGroup.daily, times, dates),
                )
            ) {
                to_remove.push(identifier);
            }
        }
        for (const identifier of scheduled_weekly) {
            if (
                !notifications_enabled ||
                !weekly_schedule.length ||
                !weekly_schedule.some(({ key, times }) =>
                    shouldBeScheduled(identifier, key, NotificationTriggerGroup.specific_days, times, [
                        today,
                        ...Object.values(week),
                    ]),
                )
            ) {
                to_remove.push(identifier);
            }
        }
        for (const identifier of scheduled_interval) {
            if (
                !notifications_enabled ||
                !interval_schedule.length ||
                !interval_schedule.some(({ key, times }) =>
                    shouldBeScheduled(identifier, key, NotificationTriggerGroup.interval, times, [today]),
                )
            ) {
                to_remove.push(identifier);
            }
        }

        for (const identifier of scheduled_appointments) {
            if (
                !tracking_events ||
                !upcoming_events.some(({ id, start_at }) =>
                    identifier.includes(makeKey(id, NotificationTriggerGroup.calendar, subDays(start_at, 1))),
                )
            ) {
                to_remove.push(identifier);
            }
        }

        syncNotifications(to_schedule, to_remove);
    }, [
        scheduling,
        mounted,
        today,
        notify_on_schedule,
        notifications_enabled,
        scheduled_daily,
        scheduled_weekly,
        scheduled_interval,
        tracking_events,
        upcoming_events,
        scheduled_appointments,
    ]);

    const value = {
        granted,
        status,
        settings,
        notifications,
        hasUnseen,
        canAskAgain,
        checkSystemSettings,
        markSeen,
        markUnSeen,
        dismiss,
        dismissSeen,
        requestPermissions,
        schedule,
        updateSettings,
    };

    return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
